import { connectToStores } from 'fluxible-addons-react';
import PropTypes from 'prop-types';
import React from 'react';
import { Dialog, DialogContent, DialogActions, Text, Button } from '@auth0/quantum-product';

import { QUANTUM_UI } from '../../../../flags';
import ApplicationStore from '../../../stores/ApplicationStore';
import UserStore from '../../../stores/UserStore';
import ApiSelector from './ApiSelector';
import ClientSelector from './ClientSelector';
import ConfiguredResources from './ConfiguredResources';
import DownloadInstructions from './DownloadInstructions';
import styles from './styles.module.styl';

const buttonStyle = QUANTUM_UI ? 'btn-success-quantum-theme' : 'btn-success';

const DownloadSampleModal = (props, context) => { 
  const {
    show,
    handleHide,
    showSelector,
    showClient,
    showApi,
    selectedClientName,
    selectedApiName,
    downloadInstructions,
    downloadInProgress,
    downloadSample,
    githubSampleLink,
    trackViewGithubSample,
    isFramedMode,
    userApis,
    userClientsByType,
    userContext,
    selectedApiId,
    selectedClientId,
    dashboardApisUrl,
  } = props;

  const handleUserResourceChange = (action, selectedId) => {
    context.executeAction(action, { selectedId });
  };

  let userClientsCount = 0;
  Object.keys(userClientsByType).forEach((key) => {
    userClientsCount += userClientsByType[key].length;
  });

  const shouldShowClient = showClient && userClientsCount > 0;
  const shouldShowApi = showApi && userApis.length > 0;

  return (
    <Dialog 
      id="qs-sample-modal" 
      open={show} 
      onClose={handleHide}
      className={`${styles.modalContainer} ${isFramedMode ? styles.modalContainerFramed : ''}`}
      title="Download sample"
    >
      <DialogContent className={styles.content}>
        {showSelector && (shouldShowClient || shouldShowApi) ? (
            <>
              <div className={styles.forms}>
                {shouldShowClient && (
                  <ClientSelector
                    handleSelected={handleUserResourceChange}
                    userClientsByType={userClientsByType}
                    selectedClientId={selectedClientId}
                  />
                )}
                {shouldShowApi && (
                  <ApiSelector
                    handleSelected={handleUserResourceChange}
                    userApis={userApis}
                    selectedApiId={selectedApiId}
                    createApiUrl={dashboardApisUrl}
                  />
                )}
              </div>
              <Text variant="body" className={styles.description}>
                You are downloading a working sample configured for&nbsp;
                <ConfiguredResources
                  showClient={shouldShowClient}
                  showApi={shouldShowApi}
                  selectedClientName={selectedClientName}
                  selectedApiName={selectedApiName}
                />
              </Text>
              <hr className={styles.divider}/>
            </>
          ) : null}
        <DownloadInstructions content={downloadInstructions} userContext={userContext} isFramedMode={isFramedMode} />
      </DialogContent>

      <DialogActions className={styles.actions}>
        <Button
          color="default"
          variant="outlined"
          size="large"
          className={`btn ${styles.btnGithub}`}
          onClick={() => {
            trackViewGithubSample();
            window.open(githubSampleLink, '_blank', 'noopener,noreferrer');
          }}
        >
          View on Github
        </Button>
        <Button
          color="primary"
          variant="contained"
          size="large"
          disabled={downloadInProgress} 
          className={`btn ${buttonStyle} ${styles.btnDownload}`}
          onClick={() => downloadSample()}
        >
          {downloadInProgress ? 'Downloading...' : 'Download'}
        </Button>
      </DialogActions>
    </Dialog> 
  );
};

DownloadSampleModal.contextTypes = {
  executeAction: PropTypes.func,
};

DownloadSampleModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleHide: PropTypes.func.isRequired,
  showSelector: PropTypes.bool,
  showClient: PropTypes.bool.isRequired,
  showApi: PropTypes.bool.isRequired,
  downloadInstructions: PropTypes.string.isRequired,
  downloadInProgress: PropTypes.bool.isRequired,
  downloadSample: PropTypes.func.isRequired,
  githubSampleLink: PropTypes.string.isRequired,
  trackViewGithubSample: PropTypes.func.isRequired,
  isFramedMode: PropTypes.bool.isRequired,
  dashboardApisUrl: PropTypes.string.isRequired,
  selectedClientName: PropTypes.string,
  selectedApiName: PropTypes.string,
  userApis: PropTypes.array.isRequired,
  userClientsByType: PropTypes.object.isRequired,
  userContext: PropTypes.object.isRequired,
  selectedApiId: PropTypes.string,
  selectedClientId: PropTypes.string,
};

export default connectToStores(DownloadSampleModal, [ApplicationStore, UserStore], (context) => {
  const applicationStore = context.getStore(ApplicationStore);
  const userStore = context.getStore(UserStore);

  return {
    userApis: userStore.getApis(),
    userContext: userStore.getUserContext(),
    selectedClientName: userStore.getSelectedClientName(),
    selectedApiName: userStore.getSelectedApiName(),
    selectedApiId: userStore.getSelectedApiId(),
    selectedClientId: userStore.getSelectedClientId(),
    dashboardApisUrl: applicationStore.getDashboardApisUrl(),
  };
});